import React, { useState } from 'react';
import {
    IconButton,
    Typography,
    Button,
    Paper,
    Grid
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { smallScreenWidth } from '../constants/AppConstants';
import OpenSearchDropdown from '../components/OpenSearchDropdown'
import fetchFromAthena from '../functions/FetchFromAthena'
import LoadingButton from '../components/LoadingButton'
import LengthOfServiceDisplay from '../components/LengthOfServiceDisplay';
import DateInput from '../components/DateInput';

const defaultHeaders = {
    company: null,
    employee: null,
    prm_job_code: null,
    prm_through_date: ''
}

export default function LengthOfService(props) {
    const { credentials } = props.fetchInitialData
    const [data, setData] = useState(null)
    const [headers, setHeaders] = useState(defaultHeaders)
    const [clearHeader, setClearHeader] = useState(false)
    const [clearTrigger, setClearTrigger] = useState(false)
    const [isLoading, setLoading] = React.useState(false)

    const handleSubmit = async () => {
        
        if (!headers.employee || !headers.company) {
            props.createSnack('Company and Employee are required', 'warning', 3000)
            return
        }
        
        if (!headers.prm_job_code) {
            props.createSnack('Job Code is required', 'warning', 3000)
            return
        }
        
        if (!headers.prm_through_date) {
            props.createSnack('Through Date is required', 'warning', 3000)
            return
        }
        
        setLoading(true)

        const queries = ['employee_length_of_service']

        try {
            const results = await Promise.allSettled(
                queries.map(query =>
                    fetchFromAthena({
                        queryName: query,
                        headers: {
                            company: headers.company,
                            employee: headers.employee,
                            prm_job_code: headers.prm_job_code,
                            prm_through_date: headers.prm_through_date
                        },
                        accessToken: credentials.user.accessToken,
                        credentials: credentials
                    })
                )
            )
            
            // Check if the promise was fulfilled
            const firstResult = results[0]
            if (firstResult.status === 'rejected') {
                console.error('API call failed:', firstResult.reason)
                props.createSnack('Failed to fetch data', 'error', 3000)
                setData(null)
                setLoading(false)
                return
            }
            
            // Extract the data array from the response
            // fetchFromAthena returns resp.data from LambdaFetch, which is the full API response object
            let responseData = []
            
            if (firstResult.value) {
                // Check if value is already an array
                if (Array.isArray(firstResult.value)) {
                    responseData = firstResult.value
                }
                // Check if value has a data property that is an array
                else if (firstResult.value.data && Array.isArray(firstResult.value.data)) {
                    responseData = firstResult.value.data
                }
            }
            
            if (!Array.isArray(responseData) || responseData.length === 0) {
                props.createSnack('No data found', 'info', 3000)
                setData(null)
                setLoading(false)
                return
            }
            
            setData(responseData)
        } catch (error) {
            console.error('Error fetching data:', error)
            props.createSnack('An error occurred while fetching data', 'error', 3000)
            setData(null)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div style={{ padding: '1.5rem 0.5rem' }}>
            <IconButton
                style={{ borderRadius: '5px', position: 'absolute' }}
                size="small"
                onClick={() => props.history.push('/')}
                aria-label="delete"
            >
                <ArrowBackIcon />
                {props.fetchInitialData.credentials.appWidth > smallScreenWidth && 'Dashboard'}
            </IconButton>
            <Typography variant="h5" style={{ textAlign: 'center' }}>
                Length of Service
            </Typography>

            <div style={{ margin: '1rem' }}>
                <Paper
                    elevation={0}
                    style={{ marginTop: '2rem', width: '100%', padding: '1rem' }}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} style={{ position: 'relative' }}>
                            <div style={{ margin: '0 1rem 0 0' }}>
                                <Grid
                                    container
                                    alignContent='center'
                                    alignItems='flex-start'
                                    spacing={3}
                                >
                                    <Grid item>
                                        <OpenSearchDropdown
                                            id='company'
                                            required
                                            width='250px'
                                            label='Company'
                                            page="EMPLOYEE"
                                            entity="COMPANY"
                                            searchFields={['DISPLAY']}
                                            accessToken={credentials.user.accessToken}
                                            credentials={credentials}
                                            clearTrigger={clearTrigger}
                                            onChange={(val) => {
                                                setHeaders({ ...headers, company: val?.VALUE || null })
                                            }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <OpenSearchDropdown
                                            key={`employee-${headers.company}`}
                                            id='employee'
                                            required
                                            disabled={!headers.company}
                                            width='300px'
                                            label='Employee'
                                            page="EMPLOYEE"
                                            entity="EMPLOYEE"
                                            searchFields={['DISPLAY']}
                                            accessToken={credentials.user.accessToken}
                                            credentials={credentials}
                                            clearTrigger={clearTrigger}
                                            filters={{
                                                COMPANY: headers.company
                                            }}
                                            onChange={(val) => {
                                                setHeaders({ ...headers, employee: val?.VALUE || null })
                                            }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <OpenSearchDropdown
                                            key={`job_code-${headers.company}-${headers.employee}`}
                                            id='employee_job_code'
                                            required={true}
                                            disabled={!headers.company || !headers.employee}
                                            width='300px'
                                            label='Job Code'
                                            page="LOS"
                                            entity="JOBCODE"
                                            searchFields={['DISPLAY']}
                                            accessToken={credentials.user.accessToken}
                                            credentials={credentials}
                                            clearTrigger={clearTrigger}
                                            filters={{
                                                COMPANY: headers.company,
                                                EMPLOYEE: headers.employee
                                            }}
                                            onChange={(val) => {
                                                setHeaders({ ...headers, prm_job_code: val?.VALUE || null })
                                            }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <DateInput
                                            id='prm_through_date'
                                            key={`prm_through_date-${clearHeader}`}
                                            type={'date'}
                                            disabled={!headers.company}
                                            required={true}
                                            label={'Through Date'}
                                            value={headers.prm_through_date || ''}
                                            onChange={e => {
                                                setHeaders({
                                                    ...headers,
                                                    prm_through_date: !!e.target.value ? e.target.value : ''
                                                })
                                            }}
                                            variant='standard'
                                            width='150px'
                                        />
                                    </Grid>
                                </Grid>
                            </div>

                        </Grid>
                        <div style={{ marginTop: '1rem' }}>
                            <LoadingButton
                                disabled={!headers.company || !headers.employee || !headers.prm_job_code || !headers.prm_through_date}
                                label='Submit'
                                isLoading={isLoading}
                                color='primaryVLButton'
                                fn={handleSubmit}
                            />
                            {!!(headers.company || headers.employee || headers.prm_job_code || headers.prm_through_date) && (
                                <Button
                                    onClick={() => {
                                        setClearHeader(!clearHeader)
                                        setHeaders(defaultHeaders)
                                        setClearTrigger(prev => !prev)
                                    }}
                                    style={{ marginLeft: '0.5rem' }}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </Grid>
                </Paper>
                {data && Array.isArray(data) && data.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                        <LengthOfServiceDisplay data={data} />
                    </div>
                )}

            </div>
        </div>
    );
}
